/**
 * A titled card section — the page-level rhythm the whole app is built from.
 *
 * Ported from `components/ui/Section.tsx`. The shape is unchanged because the layout it
 * produces is the brand: a heading floating on the video with its content in a frosted card
 * below it. What changed is who owns each half — MUI owns the typography and the surface,
 * Tailwind owns the spacing.
 *
 * The heading and description sit *on the video*, not on the card, so they carry
 * `.text-on-video`. That class is not decoration: the frame behind any given word is not
 * knowable in advance, so legibility has to come from the glyphs rather than from a backdrop.
 */

import { Card, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface SectionProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}

export function Section({ title, description, children }: SectionProps) {
  return (
    <Stack component="section" spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="h6" component="h2" className="text-white text-on-video">
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" className="text-white/80 text-on-video">
            {description}
          </Typography>
        ) : null}
      </Stack>
      <Card sx={{ p: { xs: 2.5, sm: 4 } }}>{children}</Card>
    </Stack>
  );
}
